import React from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { CheckIcon } from '@heroicons/react/24/solid'

const plans = [
  {
    name: '5-Hour Package',
    description: 'Great for beginners getting started behind the wheel',
    price: 299,
    priceNote: '$65/hr — save with a bundle',
    features: [
      '5 hours behind-the-wheel training',
      'Pick-up and drop-off service',
      'DMV test preparation',
      'Certificate of completion',
    ],
    cta: 'Get Started',
    popular: false,
  },
  {
    name: '7-Hour Package',
    description: 'Our most popular choice for confident, road-ready drivers',
    price: 399,
    priceNote: '$65/hr — save with a bundle',
    features: [
      '7 hours behind-the-wheel training',
      'Pick-up and drop-off service',
      'DMV test preparation',
      'Certificate of completion',
    ],
    cta: 'Get Started',
    popular: true,
  },
  {
    name: '10-Hour Package',
    description: 'Maximum training for complete driving confidence',
    price: 499,
    priceNote: '$65/hr — save with a bundle',
    features: [
      '10 hours behind-the-wheel training',
      'Pick-up and drop-off service',
      'DMV test preparation',
      'Certificate of completion',
    ],
    cta: 'Get Started',
    popular: false,
  },
]

const teamPlan = {
  name: 'Team Behind the Wheel',
  description: 'Group program with team-based behind-the-wheel training',
  price: 359,
  features: [
    'Team-based behind-the-wheel training',
    'Pick-up and drop-off service',
    'DMV test preparation',
    'Certificate of completion',
    'Parent orientation session',
  ],
  cta: 'Get Started',
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 },
  },
}

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 },
  },
}

export default function CoursesContent() {
  return (
    <div className="py-20 bg-background">
      <div className="container mx-auto px-4">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="text-accent font-heading font-semibold text-sm uppercase tracking-wider mb-2">
            Our Packages
          </div>
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-primary mb-4">
            Transparent Pricing
          </h1>
          <p className="text-lg text-secondary-text max-w-2xl mx-auto">
            Choose the package that fits your needs. All prices include materials and certification.
          </p>
        </motion.div>

        {/* Per-Hour Badge */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="text-center mb-10"
        >
          <span className="inline-block bg-accent/10 text-primary font-heading font-semibold px-5 py-2 rounded-full text-sm">
            Single lesson: <span className="text-accent font-bold">$65 / hour</span>
          </span>
        </motion.div>

        {/* 3 Main Package Cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto"
        >
          {plans.map((plan) => (
            <motion.div
              key={plan.name}
              variants={cardVariants}
              whileHover={{ y: -8, transition: { duration: 0.2 } }}
              className={`relative p-8 rounded-lg shadow-soft hover:shadow-lift transition-all ${
                plan.popular ? 'bg-primary text-white scale-105' : 'bg-white'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-accent text-primary px-4 py-1 rounded-full text-xs font-heading font-bold">
                  MOST POPULAR
                </div>
              )}
              <h3 className={`text-2xl font-heading font-bold mb-2 ${plan.popular ? 'text-white' : 'text-primary'}`}>
                {plan.name}
              </h3>
              <p className={`mb-6 ${plan.popular ? 'text-white/80' : 'text-secondary-text'}`}>
                {plan.description}
              </p>
              <div className="mb-1">
                <span className={`text-5xl font-heading font-bold ${plan.popular ? 'text-white' : 'text-primary'}`}>
                  ${plan.price}
                </span>
                <span className={`text-lg ${plan.popular ? 'text-white/80' : 'text-secondary-text'}`}> USD</span>
              </div>
              {plan.priceNote && (
                <p className={`text-xs mb-5 ${plan.popular ? 'text-white/60' : 'text-secondary-text/70'}`}>
                  {plan.priceNote}
                </p>
              )}
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2">
                    <CheckIcon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${plan.popular ? 'text-accent' : 'text-success'}`} />
                    <span className={plan.popular ? 'text-white/90' : 'text-secondary-text'}>
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>
              <Link
                to={`/register?package=${encodeURIComponent(plan.name)}`}
                className={`block w-full text-center py-3 rounded-full font-heading font-semibold transition-all hover:scale-105 ${
                  plan.popular
                    ? 'bg-accent text-primary hover:shadow-lift'
                    : 'bg-primary text-white hover:bg-primary/90'
                }`}
              >
                {plan.cta}
              </Link>
            </motion.div>
          ))}
        </motion.div>

        {/* Per-Hour & Team Behind the Wheel Cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto mt-10"
        >
          {/* Per-Hour Card */}
          <motion.div
            variants={cardVariants}
            whileHover={{ y: -8, transition: { duration: 0.2 } }}
            className="relative p-8 rounded-lg shadow-soft hover:shadow-lift transition-all bg-white"
          >
            <h3 className="text-2xl font-heading font-bold mb-2 text-primary">Per Hour</h3>
            <p className="mb-6 text-secondary-text">
              Flexible single-lesson booking, no package required
            </p>
            <div className="mb-6">
              <span className="text-5xl font-heading font-bold text-primary">$65</span>
              <span className="text-lg text-secondary-text"> / hour</span>
            </div>
            <ul className="space-y-3 mb-8">
              {[
                'One hour behind-the-wheel training',
                'Pick-up and drop-off service',
                'Flexible scheduling',
                'Professional certified instructor',
              ].map((feature) => (
                <li key={feature} className="flex items-start gap-2">
                  <CheckIcon className="w-5 h-5 flex-shrink-0 mt-0.5 text-success" />
                  <span className="text-secondary-text">{feature}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/register?package=Per+Hour"
              className="block w-full text-center py-3 rounded-full font-heading font-semibold transition-all hover:scale-105 bg-primary text-white hover:bg-primary/90"
            >
              Book a Lesson
            </Link>
          </motion.div>

          {/* Team Behind the Wheel Card */}
          <motion.div
            variants={cardVariants}
            whileHover={{ y: -8, transition: { duration: 0.2 } }}
            className="relative p-8 rounded-lg shadow-soft hover:shadow-lift transition-all bg-white"
          >
            <h3 className="text-2xl font-heading font-bold mb-2 text-primary">
              {teamPlan.name}
            </h3>
            <p className="mb-6 text-secondary-text">{teamPlan.description}</p>
            <div className="mb-6">
              <span className="text-5xl font-heading font-bold text-primary">${teamPlan.price}</span>
              <span className="text-lg text-secondary-text"> USD</span>
            </div>
            <ul className="space-y-3 mb-8">
              {teamPlan.features.map((feature) => (
                <li key={feature} className="flex items-start gap-2">
                  <CheckIcon className="w-5 h-5 flex-shrink-0 mt-0.5 text-success" />
                  <span className="text-secondary-text">{feature}</span>
                </li>
              ))}
            </ul>
            <Link
              to={`/register?package=${encodeURIComponent(teamPlan.name)}`}
              className="block w-full text-center py-3 rounded-full font-heading font-semibold transition-all hover:scale-105 bg-primary text-white hover:bg-primary/90"
            >
              {teamPlan.cta}
            </Link>
          </motion.div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center text-sm text-secondary-text mt-8"
        >
          * Payment plans available. Contact us for more details.
        </motion.p>

      </div>
    </div>
  )
}
